import { addDoc, collection, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import type {
  EvaluationScore,
  Procurement,
  ProcurementSettings,
  Rubric,
  RubricCriteria,
  SupplierDocument,
} from '../types/procurement';
import type { StagedSupplier } from '../utils/fileUploadUtils';
import FileConverterService from './fileConverter';
import { firestore } from './firebase';
import httpClient from './http/httpClient';
import storageService from './storageService';
import { unifiedStorageService } from './unifiedStorageService';

export const getRubricTemplate = async (
  clientId: string,
  templateId: string
): Promise<{ criteria?: RubricCriteria[] } | null> => {
  const docRef = doc(firestore, 'clientSettings', clientId, 'rubricTemplates', templateId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return docSnap.data() as { criteria?: RubricCriteria[] };
  }
  return null;
};

export const updateProcurement = async (
  procurementId: string,
  updates: Partial<Procurement>
): Promise<void> => {
  const procurementRef = doc(firestore, 'procurements', procurementId);
  const now = new Date().toISOString();
  await updateDoc(procurementRef, {
    ...updates,
    updatedAt: now,
  });
};

export const archiveProcurement = async (procurementId: string): Promise<void> => {
  const procurementRef = doc(firestore, 'procurements', procurementId);
  const now = new Date().toISOString();
  await updateDoc(procurementRef, {
    status: 'ARCHIVED',
    updatedAt: now,
  });
};

export const deleteProcurement = async (procurementId: string): Promise<void> => {
  await httpClient.post('/deleteProcurement', { procurementId });
};

export const createProcurement = async (
  procurementData: Omit<Procurement, 'createdAt' | 'updatedAt'> & { id?: string }
): Promise<Procurement> => {
  const procurementsRef = collection(firestore, 'procurements');
  const now = new Date().toISOString();

  const newProcurement = {
    ...procurementData,
    createdAt: now,
    updatedAt: now,
  };

  let docId = procurementData.id;
  if (docId) {
    await setDoc(doc(procurementsRef, docId), newProcurement);
  } else {
    const docRef = await addDoc(procurementsRef, newProcurement);
    docId = docRef.id;
  }

  return {
    ...newProcurement,
    id: docId,
  } as Procurement;
};

export const createRubric = async (
  procurementId: string,
  criteria: RubricCriteria[],
  userId: string
): Promise<Rubric> => {
  const procurementsRef = collection(firestore, 'procurements');
  const rubricRef = doc(procurementsRef, procurementId, 'rubric', 'default');
  const now = new Date().toISOString();

  const rubricData = {
    procurementId,
    criteria,
    updatedAt: now,
    updatedBy: userId,
  };

  await setDoc(rubricRef, rubricData);
  return {
    id: rubricRef.id,
    ...rubricData,
  };
};

export const extractComplianceMatrix = async (
  procurementId: string,
  clientId: string,
  userId: string,
  supplierId: string,
  documentPaths: string[],
  requirements: string[],
  settings?: ProcurementSettings
): Promise<{ success: boolean; compliance_matrix?: Record<string, string>; error?: string }> => {
  return httpClient.post('/extract-compliance-matrix-service', {
    procurementId,
    clientId,
    userId,
    supplierId,
    documentPaths,
    requirements,
    settings,
  });
};

export const runNliRedTeam = async (
  procurementId: string,
  clientId: string,
  userId: string,
  supplierId: string,
  documentPaths: string[],
  settings?: ProcurementSettings
): Promise<{
  success: boolean;
  contradictions?: { risk: string; description: string; citations?: string[] }[];
  error?: string;
}> => {
  return httpClient.post('/run-nli-red-team-service', {
    procurementId,
    clientId,
    userId,
    supplierId,
    documentPaths,
    settings,
  });
};

export const evaluateSupplierClaims = async (
  procurementId: string,
  clientId: string,
  userId: string,
  supplierId: string,
  rubric: Rubric,
  documentPaths: string[],
  settings?: ProcurementSettings
): Promise<{
  success: boolean;
  evaluations?: EvaluationScore[];
  supplier_summary?: string;
  error?: string;
}> => {
  return httpClient.post('/evaluate-supplier-claims-service', {
    procurementId,
    clientId,
    userId,
    supplierId,
    rubric,
    documentPaths,
    settings,
  });
};

export const saveEvaluation = async (
  procurementId: string,
  supplierId: string,
  evaluationId: string,
  evaluatorId: string,
  scores: EvaluationScore[],
  overallComments: string,
  status: 'DRAFT' | 'SUBMITTED' = 'DRAFT'
): Promise<{ success: boolean; error?: string }> => {
  try {
    const evalRef = doc(
      firestore,
      'procurements',
      procurementId,
      'evaluations',
      evaluationId || crypto.randomUUID()
    );

    await setDoc(
      evalRef,
      {
        id: evalRef.id,
        procurementId,
        supplierId,
        evaluatorId,
        scores,
        overallComments,
        status,
        updatedAt: new Date().toISOString(),
      },
      { merge: true }
    );

    return { success: true };
  } catch (error: unknown) {
    return { success: false, error: (error as Error).message };
  }
};

export const saveAssessment = async (
  procurementId: string,
  assessmentId: string,
  supplierId: string,
  type: string,
  data: Record<string, unknown>
): Promise<{ success: boolean; error?: string }> => {
  try {
    const assessmentRef = doc(
      firestore,
      'procurements',
      procurementId,
      'assessments',
      assessmentId || crypto.randomUUID()
    );

    await setDoc(
      assessmentRef,
      {
        id: assessmentRef.id,
        procurementId,
        supplierId,
        type,
        ...data,
        updatedAt: new Date().toISOString(),
      },
      { merge: true }
    );

    return { success: true };
  } catch (error: unknown) {
    return { success: false, error: (error as Error).message };
  }
};

export const synthesizeModerationReport = async (
  procurementId: string,
  clientId: string,
  userId: string,
  payloadData: Record<string, unknown>,
  settings?: ProcurementSettings
): Promise<{ success: boolean; report_text?: string; error?: string }> => {
  return httpClient.post(
    '/synthesize-moderation-report-service',
    {
      action: 'synthesize_report',
      procurementId,
      clientId,
      userId,
      evaluations: payloadData,
      settings,
    },
    {
      headers: {},
    }
  );
};

export const generateQuickAnalysis = async (
  procurementId: string,
  clientId: string,
  userId: string,
  payloadData: Record<string, unknown>,
  settings?: ProcurementSettings
): Promise<{ success: boolean; analysis_text?: string; error?: string }> => {
  return httpClient.post(
    '/synthesize-moderation-report-service',
    {
      action: 'quick_analysis',
      procurementId,
      clientId,
      userId,
      evaluations: payloadData,
      settings,
    },
    {
      headers: {},
    }
  );
};

export const extractRubricFromRfp = async (
  procurementId: string,
  rfpText: string
): Promise<{ success: boolean; rubric_criteria?: RubricCriteria[]; error?: string }> => {
  return httpClient.post('/rfp-builder-service', {
    action: 'extract_rubric',
    procurementId,
    rfpText,
  });
};

export const anticipateSupplierQa = async (
  procurementId: string,
  rfpText: string
): Promise<{
  success: boolean;
  questions?: { quote: string; reasoning: string }[];
  error?: string;
}> => {
  return httpClient.post('/rfp-builder-service', {
    action: 'anticipate_qa',
    procurementId,
    rfpText,
  });
};

export const draftRfpSection = async (
  procurementId: string,
  sectionTitle: string,
  userInstruction: string,
  currentContent: string,
  wordCount?: number,
  domain: string = 'all'
): Promise<{
  success: boolean;
  section_content?: string;
  citations?: string[];
  error?: string;
}> => {
  return httpClient.post('/rfp-builder-service', {
    action: 'draft_section',
    procurementId,
    sectionTitle,
    userInstruction,
    currentContent,
    wordCount,
    domain,
  });
};

export const exportReportDocx = async (
  procurementId: string,
  htmlContent: string
): Promise<{ success: boolean; download_url?: string; remote_path?: string; error?: string }> => {
  return httpClient.post(
    '/export-report-docx-service',
    {
      procurementId,
      htmlContent,
    },
    {
      headers: {},
    }
  );
};

export const exportReportPresentation = async (
  procurementId: string,
  payloadData: Record<string, unknown>
): Promise<{
  success: boolean;
  pptx_download_url?: string;
  html_download_url?: string;
  pptx_url?: string;
  html_url?: string;
  error?: string;
}> => {
  return httpClient.post(
    '/export-report-presentation-service',
    {
      procurementId,
      payloadData,
    },
    {
      headers: {},
    }
  );
};

export const assembleContextClauses = async (
  procurementId: string,
  query: string,
  domain: string = 'all'
): Promise<{
  success: boolean;
  clauses?: { clause_text: string; explanation: string }[];
  error?: string;
}> => {
  return httpClient.post('/rfp-builder-service', {
    action: 'assemble_clauses',
    procurementId,
    query,
    domain,
  });
};

export const generateReturnableSchedule = async (
  procurementId: string,
  rubricCriteria: RubricCriteria[]
): Promise<{
  success: boolean;
  schedule?: { question: string; expected_format: string; [key: string]: unknown }[];
  error?: string;
}> => {
  return httpClient.post('/rfp-builder-service', {
    action: 'generate_schedule',
    procurementId,
    rubricCriteria,
  });
};
export const exportRfpToDocx = async (
  procurementId: string,
  blocks: { id: string; title: string; content: string }[],
  templateId?: string
): Promise<{ success: boolean; file_url?: string; error?: string }> => {
  return httpClient.post('/rfp-builder-service', {
    action: 'export_docx',
    procurementId,
    blocks,
    templateId,
  });
};

export const uploadBulkSupplierSubmissions = async (
  clientId: string,
  teamId: string,
  userId: string,
  procurementId: string,
  stagedSuppliers: StagedSupplier[],
  onProgress?: (progress: number, total: number) => void
): Promise<void> => {
  let completed = 0;
  const totalFiles = stagedSuppliers.reduce(
    (acc, s) => acc + s.files.filter((f) => f.status === 'ok').length,
    0
  );

  for (const supplier of stagedSuppliers) {
    const validFiles = supplier.files.filter((f) => f.status === 'ok');
    if (validFiles.length === 0) {
      continue;
    }

    const supplierId = crypto.randomUUID();
    const uploadedDocs: SupplierDocument[] = [];

    for (const stagedFile of validFiles) {
      const path = `clients/${clientId}/teams/${teamId}/users/${userId}/procurements/${procurementId}/suppliers/${supplierId}/${stagedFile.name}`;

      // Use the new unified storage service to securely bypass legacy CSE
      const finalPath = await unifiedStorageService.uploadFile(path, stagedFile.file as File);
      // Wait, firebase storage handles paths starting with `gs://` or just the relative path
      // storageService returns the final gs:// path if it's GCS. We actually need to get the exact URI.
      // Let's use the result from unifiedStorageService.

      uploadedDocs.push({
        id: crypto.randomUUID(),
        name: stagedFile.name,
        url: finalPath,
        status: 'ok',
        conversionStatus: FileConverterService.shouldConvertFile(stagedFile.name)
          ? 'pending'
          : 'success',
      });

      completed++;
      if (onProgress) {
        onProgress(completed, totalFiles);
      }
    }

    const supplierRef = doc(firestore, 'procurements', procurementId, 'suppliers', supplierId);

    // Save the supplier first so the backend can find and update it
    await setDoc(supplierRef, {
      id: supplierId,
      procurementId,
      name: supplier.supplierName,
      status: 'SUBMITTED',
      submissionDate: new Date().toISOString(),
      documents: uploadedDocs,
    });

    // Attempt to trigger conversion for files that need it
    const filesToConvert = uploadedDocs
      .filter((doc) => doc.conversionStatus === 'pending')
      .map((doc) => doc.url);

    if (filesToConvert.length > 0) {
      try {
        // Trigger conversion async so we don't block the UI entirely,
        // but we'll mark them as processing or failed initially based on trigger success.
        const conversionResponse = await FileConverterService.triggerBatchFileConversion(
          clientId,
          filesToConvert
        );

        if (!conversionResponse.success) {
          // If the trigger failed entirely, update the doc to show the error
          const failedDocs = uploadedDocs.map((d) =>
            d.conversionStatus === 'pending'
              ? {
                  ...d,
                  conversionStatus: 'failed' as const,
                  conversionError:
                    conversionResponse.error || 'Failed to trigger conversion pipeline',
                }
              : d
          );
          await setDoc(supplierRef, { documents: failedDocs }, { merge: true });
        }
      } catch (err: unknown) {
        const failedDocs = uploadedDocs.map((d) =>
          d.conversionStatus === 'pending'
            ? {
                ...d,
                conversionStatus: 'failed' as const,
                conversionError: (err as Error).message || 'Failed to trigger conversion pipeline',
              }
            : d
        );
        await setDoc(supplierRef, { documents: failedDocs }, { merge: true });
      }
    }
  }
};

export const deleteSupplierDocument = async (
  procurementId: string,
  supplierId: string,
  documentId: string,
  documents: SupplierDocument[]
): Promise<void> => {
  const docToDelete = documents.find((d) => d.id === documentId);
  if (docToDelete) {
    await storageService.deleteFile(docToDelete.url);
  }

  const supplierRef = doc(firestore, 'procurements', procurementId, 'suppliers', supplierId);
  await updateDoc(supplierRef, {
    documents: documents.filter((d) => d.id !== documentId),
  });
};

export const uploadRFPForProcurement = async (
  procurementId: string,
  clientId: string,
  documentPaths: string[]
): Promise<{ success: boolean; message?: string }> => {
  return httpClient.post(
    '/processProcurementUpload',
    {
      procurementId,
      clientId,
      documentPaths,
    },
    {
      headers: {},
    }
  );
};

export const generateClarifications = async (
  procurementId: string,
  clientId: string,
  questions: { id: string; text: string }[],
  evidenceContext: string,
  forceGuess: boolean = false
): Promise<{
  success: boolean;
  clarifications?: { id: string; question: string; answer: string }[];
  error?: string;
}> => {
  return httpClient.post(
    '/generateClarifications',
    {
      procurementId,
      clientId,
      questions,
      evidenceContext,
      forceGuess,
    },
    {
      headers: {},
    }
  );
};

export const saveClarificationsToGlobalQA = async (
  procurementId: string,
  clientId: string,
  clarifications: { id: string; question: string; answer: string }[]
): Promise<{ success: boolean; path?: string; error?: string }> => {
  return httpClient.post('/saveClarificationsToGlobalQA', {
    procurementId,
    clientId,
    clarifications,
  });
};

export const extractClarificationQuestions = async (
  procurementId: string,
  clientId: string,
  documentPath: string
): Promise<{ success: boolean; questions?: string[]; error?: string }> => {
  return httpClient.post(
    '/extractClarificationQuestions',
    {
      procurementId,
      clientId,
      documentPath,
    },
    {
      headers: {},
    }
  );
};

export const updateSupplierStatus = async (
  procurementId: string,
  supplierId: string,
  status: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const supplierRef = doc(firestore, 'procurements', procurementId, 'suppliers', supplierId);

    await updateDoc(supplierRef, { status });
    return { success: true };
  } catch (error: unknown) {
    return { success: false, error: (error as Error).message };
  }
};

export const assignEvaluator = async (
  procurementId: string,
  supplierId: string,
  userId: string,
  userName: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { addDoc, collection, doc, getDoc, setDoc } = await import('firebase/firestore');
    const { firestore: db } = await import('./firebase');

    // 1. Fetch Procurement Data to check for Encryption
    const procurementRef = doc(db, 'procurements', procurementId);
    const procSnap = await getDoc(procurementRef);
    if (!procSnap.exists()) {
      throw new Error('Procurement not found');
    }
    const procData = procSnap.data();

    // 2. Share PEK if envelope encryption is enabled
    if (procData.encryptionMode === 'envelope' && procData.wrappedKey && procData.keyIV) {
      // Fetch Target User's Public Key
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        throw new Error('Target user not found');
      }
      const targetPublicKeyJWK = userSnap.data().publicKey;

      if (!targetPublicKeyJWK) {
        throw new Error('Target user has not set up their encryption keys yet.');
      }

      // Import Target Public Key

      // Wrap PEK with Target Public Key

      // Write Shared Key to Subcollection
      const keyDocRef = doc(db, 'procurements', procurementId, 'keys', userId);
      await setDoc(keyDocRef, {
        algorithm: 'RSA-OAEP',
        updatedAt: new Date().toISOString(),
      });
    }

    const assignmentsRef = collection(db, 'procurements', procurementId, 'evaluatorAssignments');

    await addDoc(assignmentsRef, {
      procurementId,
      supplierId,
      userId,
      userName,
      dateAssigned: new Date().toISOString(),
    });
    return { success: true };
  } catch (error: unknown) {
    return { success: false, error: (error as Error).message };
  }
};

export const removeEvaluatorAssignment = async (
  procurementId: string,
  assignmentId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { deleteDoc, doc } = await import('firebase/firestore');
    const { firestore: db } = await import('./firebase');
    const assignmentRef = doc(
      db,
      'procurements',
      procurementId,
      'evaluatorAssignments',
      assignmentId
    );

    await deleteDoc(assignmentRef);
    return { success: true };
  } catch (error: unknown) {
    return { success: false, error: (error as Error).message };
  }
};
